import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { useFamily } from '../contexts/FamilyContext';
import { UserPlus, Users, Pencil, Check, X } from 'lucide-react';

export default function FamilyManager() {
    const [newMemberName, setNewMemberName] = useState('');
    const { addMember, renameMember, familyData } = useFamily();
    const [isOpen, setIsOpen] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [editingName, setEditingName] = useState('');

    async function handleAddMember(e) {
        e.preventDefault();
        if (!newMemberName.trim()) return;

        await addMember(newMemberName);
        setNewMemberName('');
    }

    function startEdit(member) {
        setEditingId(member.id);
        setEditingName(member.name);
    }

    async function confirmRename(memberId) {
        await renameMember(memberId, editingName);
        setEditingId(null);
    }

    function cancelEdit() {
        setEditingId(null);
        setEditingName('');
    }

    return (
        <>
            <button
                onClick={() => setIsOpen(true)}
                className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors"
            >
                <Users className="h-5 w-5" />
                <span className="hidden md:inline">Manage Family</span>
            </button>

            {isOpen && createPortal(
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                    <div className="bg-slate-800 rounded-lg p-6 max-w-md w-full border border-slate-700 shadow-xl">
                        <div className="flex justify-between items-center mb-6">
                            <h3 className="text-xl font-bold text-white flex items-center gap-2">
                                <Users className="h-6 w-6 text-blue-400" />
                                Family Members
                            </h3>
                            <button
                                onClick={() => { setIsOpen(false); cancelEdit(); }}
                                className="text-slate-400 hover:text-white"
                            >
                                ✕
                            </button>
                        </div>

                        <div className="space-y-4 mb-6">
                            {familyData?.members?.map(member => (
                                <div key={member.id} className="flex items-center justify-between bg-slate-900 p-3 rounded border border-slate-700">
                                    {editingId === member.id ? (
                                        <input
                                            autoFocus
                                            value={editingName}
                                            onChange={(e) => setEditingName(e.target.value)}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter') confirmRename(member.id);
                                                if (e.key === 'Escape') cancelEdit();
                                            }}
                                            className="flex-1 bg-slate-800 border border-blue-500 rounded px-2 py-1 text-white focus:outline-none mr-2"
                                        />
                                    ) : (
                                        <span className="text-white font-medium">{member.name}</span>
                                    )}
                                    <div className="flex items-center gap-2">
                                        {editingId === member.id ? (
                                            <>
                                                <button onClick={() => confirmRename(member.id)} className="text-green-400 hover:text-green-300">
                                                    <Check className="h-4 w-4" />
                                                </button>
                                                <button onClick={cancelEdit} className="text-slate-400 hover:text-white">
                                                    <X className="h-4 w-4" />
                                                </button>
                                            </>
                                        ) : (
                                            <>
                                                <span className="text-xs bg-slate-800 text-slate-400 px-2 py-1 rounded">
                                                    {member.role === 'admin' ? 'owner' : 'member'}
                                                </span>
                                                <button onClick={() => startEdit(member)} className="text-slate-400 hover:text-white">
                                                    <Pencil className="h-4 w-4" />
                                                </button>
                                            </>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>

                        <form onSubmit={handleAddMember} className="flex gap-2">
                            <input
                                type="text"
                                placeholder="New member name..."
                                className="flex-1 bg-slate-900 border border-slate-700 rounded p-2 text-white focus:outline-none focus:border-blue-500"
                                value={newMemberName}
                                onChange={(e) => setNewMemberName(e.target.value)}
                            />
                            <button
                                type="submit"
                                className="bg-blue-600 hover:bg-blue-700 text-white p-2 rounded transition-colors"
                            >
                                <UserPlus className="h-5 w-5" />
                            </button>
                        </form>
                    </div>
                </div>,
                document.body
            )}
        </>
    );
}
